import { failure, type AuthResult } from './auth-client';
import { getSupabaseClient } from '../supabase';

/**
 * Login social — implementado e **desligado por flag**.
 *
 * O código existe agora para que o M7 ligue uma flag em vez de escrever uma feature. As
 * credenciais que faltam (Client ID/Secret do Google, Services ID e chave `.p8` da Apple) são
 * configuração de **servidor**, no Dashboard do Supabase: nada aqui depende delas para ser escrito
 * ou testado. Por isso `RF-AUTH-01` fecha **parcial** — credencial em falta, não código em falta.
 *
 * 🔴 A flag do cliente tem de espelhar `auth.external.*.enabled` do `config.toml`. Ligá-la sem
 * configurar o provider no servidor leva o utilizador a um ecrã de erro do GoTrue.
 */

export const SOCIAL_PROVIDERS = ['google', 'apple'] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

/** Fonte da flag. Em runtime é `import.meta.env`; nos testes, um objeto injetado. */
export interface SocialEnvSource {
  VITE_AUTH_SOCIAL_ENABLED?: string | undefined;
}

/**
 * Lê a flag sendo explícito sobre o que conta como "ligada".
 *
 * A variável chega do bundle como **string**. O idioma tentador — `Boolean(valor)` — faria
 * `VITE_AUTH_SOCIAL_ENABLED=false` ligar o social, porque `'false'` é uma string não vazia. Só
 * `true` e `1` ligam; tudo o resto, incluindo a ausência, deixa desligado.
 */
export function isSocialEnabled(source: SocialEnvSource): boolean {
  const valor = source.VITE_AUTH_SOCIAL_ENABLED?.trim().toLowerCase();
  return valor === 'true' || valor === '1';
}

/**
 * Destino do retorno do OAuth.
 *
 * Tem de constar das `additional_redirect_urls` do `config.toml`; um destino não listado é recusado
 * pelo GoTrue. Em nativo o esquema `pt.powerfulconnect.app://` já está registado lá.
 */
function callbackUrl(): string {
  if (typeof window === 'undefined') return '/auth/callback';
  return `${window.location.origin}/auth/callback`;
}

/**
 * Inicia o fluxo OAuth do provider.
 *
 * Com a flag desligada **não chama o SDK** e devolve `social_disabled` — a tela usa esse código
 * para mostrar o botão desabilitado com o badge "brevemente", em vez de o esconder.
 */
export async function signInWithProvider(
  provider: SocialProvider,
  source: SocialEnvSource,
): Promise<AuthResult<{ url: string | null }>> {
  if (!isSocialEnabled(source)) {
    return failure('social_disabled', 'Autenticação social ainda não disponível.');
  }

  try {
    const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
      provider,
      options: { redirectTo: callbackUrl() },
    });

    if (error) {
      return failure('unknown', error.message ?? 'Não foi possível iniciar sessão com este fornecedor.');
    }
    return { ok: true, data: { url: data?.url ?? null } };
  } catch {
    return failure('network', 'Sem ligação ao fornecedor de autenticação.');
  }
}
